import axios from 'axios';

import { refreshAccessToken } from './tokenRefresh.js';

const CSG_GATEWAY_URL = import.meta.env.VITE_CSG_GATEWAY_URL || 'http://localhost:3000';

// Backwards-compat alias — some callers (e.g. onboarding.js) import
// AGENT_BACKEND_URL from here. It resolves to the gateway base URL because all
// agent-backend traffic is proxied through the gateway. Mirrors settingsApi.js.
export const AGENT_BACKEND_URL = CSG_GATEWAY_URL;

/**
 * Axios instance configured for CSG Aggregation Gateway
 * Uses OAuth2 Bearer tokens from MSAL for authentication
 */
const gatewayClient = axios.create({
  baseURL: CSG_GATEWAY_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
});

// Request interceptor for logging and OAuth2 token injection
gatewayClient.interceptors.request.use(
  (config) => {
    // Inject OAuth2 Bearer token from localStorage (set by AuthContext)
    const accessToken = localStorage.getItem('accessToken');
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }

    // Inject department header for per-department KB isolation
    const userDepartment = localStorage.getItem('userDepartment');
    if (userDepartment) {
      config.headers['X-User-Department'] = userDepartment;
    }

    if (import.meta.env.DEV) {
      console.log(`[Gateway] ${config.method?.toUpperCase()} ${config.url}`);
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling and token refresh
gatewayClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // 401 not yet retried: one silent refresh-and-retry before giving up. An
    // upload batch that straddles token expiry (2026-08-20 walk-round: photos
    // 7-10 of 10 died) must continue on a fresh token, not die mid-loop.
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      const freshToken = await refreshAccessToken();
      if (freshToken) {
        // Persist here too (not just in the refresher): the retried request
        // passes back through the request interceptor, which re-reads
        // localStorage — the two must agree on the fresh token.
        localStorage.setItem('accessToken', freshToken);
        originalRequest.headers.Authorization = `Bearer ${freshToken}`;
        return gatewayClient(originalRequest);
      }

      console.warn('[Gateway] 401 Unauthorized - no fresh token available. Clearing session...');

      // Clear expired token from storage
      localStorage.removeItem('accessToken');

      // Redirect to login page to trigger MSAL re-authentication — unless the
      // request opted out (skipAuthRedirect: long-running batches like the
      // walk-round photo filing surface per-item guidance instead of being
      // navigated away mid-loop).
      if (!originalRequest.skipAuthRedirect && window.location.pathname !== '/login') {
        console.log('[Gateway] Redirecting to login...');
        window.location.assign('/login');
      }

      return Promise.reject(error);
    }

    if (error.response) {
      // Server responded with error status
      console.error(`[Gateway] Error ${error.response.status}:`, error.response.data);
    } else if (error.request) {
      // Request made but no response
      console.error('[Gateway] No response received:', error.message);
    } else {
      // Error setting up request
      console.error('[Gateway] Request error:', error.message);
    }
    return Promise.reject(error);
  }
);

/**
 * Call an MCP tool through the CSG Gateway
 * @param {string} serverName - MCP server name (e.g., 'CIPP-MCP', 'Sophos-MCP')
 * @param {string} toolName - Tool name to call
 * @param {object} toolArguments - Arguments to pass to the tool
 * @param {object} options - Optional request options
 * @param {number} options.timeout - Custom timeout in milliseconds (default: 30000)
 * @param {boolean} options.skipAuthRedirect - When true, an unrecoverable 401
 *   rejects instead of navigating to /login — for batch loops that must finish
 *   and surface per-item guidance (walk-round photo filing)
 * @returns {Promise<any>} Tool result
 */
export const callMCPTool = async (serverName, toolName, toolArguments = {}, options = {}) => {
  const { timeout, skipAuthRedirect } = options;

  const response = await gatewayClient.post('/mcp', {
    jsonrpc: '2.0',
    id: Date.now(),
    method: 'tools/call',
    params: {
      name: toolName,
      arguments: toolArguments,
    },
  }, {
    headers: {
      'X-MCP-Server': serverName,
    },
    ...(timeout && { timeout }),
    ...(skipAuthRedirect && { skipAuthRedirect: true }),
  });

  // Handle JSON-RPC response
  if (response.data.error) {
    throw new Error(response.data.error.message || 'MCP tool call failed');
  }

  const result = response.data.result;

  // Parse MCP content response - the actual data is in content[0].text as a JSON string
  if (result?.content?.[0]?.type === 'text' && result?.content?.[0]?.text) {
    try {
      return JSON.parse(result.content[0].text);
    } catch {
      // If not valid JSON, return the text as-is
      return result.content[0].text;
    }
  }

  return result;
};

/**
 * Get gateway health status
 */
export const getGatewayHealth = async () => {
  const response = await gatewayClient.get('/health');
  return response.data;
};

/**
 * Get aggregated data for a specific tenant
 * @param {string} tenantId - Tenant ID
 */
export const getAggregatedTenant = async (tenantId) => {
  const response = await gatewayClient.get(`/aggregate/tenant/${tenantId}`);
  return response.data;
};

/**
 * Get aggregated data for all tenants
 */
export const getAggregatedTenants = async () => {
  const response = await gatewayClient.get('/aggregate/tenants');
  return response.data;
};

/**
 * Get summary statistics across all tenants
 */
export const getAggregatedSummary = async () => {
  const response = await gatewayClient.get('/aggregate/summary');
  return response.data;
};

export default gatewayClient;
